import { useState } from 'react';
import { ErrorState, EmptyState } from '../../components';
import { api, useMutation } from '../../api';
import type { OfficeView } from '../../api';
import { useOffices } from '../../offices';
import { useT } from '../../i18n';
import type { TFunc } from '../../i18n';
import dict from './Settings.i18n';
import { PermissionsMatrix } from './PermissionsMatrix';

/**
 * Admin · Settings — system & integration status. Route /admin/settings (SUPER_ADMIN
 * only, route-gated). Variant-B clean design.
 *
 * READ-ONLY in Phase 1: there is no settings DB table — integration modes + provider
 * credentials live in the deploy vault (env). This screen only REPORTS configured
 * status (GET /settings/integrations) and shows static system/retention facts from
 * DECISIONS.md. There are deliberately no save buttons that do nothing.
 */

/** One editable office row: inline rename, activate/deactivate, delete. */
function OfficeRow({
  office,
  t,
  onChanged,
}: {
  office: OfficeView;
  t: TFunc;
  onChanged: () => void;
}) {
  const update = useMutation(api.offices.update);
  const remove = useMutation(api.offices.remove);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(office.name);

  const busy = update.loading || remove.loading;
  const error = update.error ?? remove.error;

  async function saveName() {
    const next = draft.trim();
    if (!next || next === office.name) {
      setEditing(false);
      setDraft(office.name);
      return;
    }
    try {
      await update.mutate(office.id, { name: next });
      setEditing(false);
      onChanged();
    } catch {
      /* error surfaced inline below */
    }
  }

  async function toggleActive() {
    try {
      await update.mutate(office.id, { active: !office.active });
      onChanged();
    } catch {
      /* error surfaced inline below */
    }
  }

  async function del() {
    if (!window.confirm(t('offices_delete_confirm'))) return;
    try {
      await remove.mutate(office.id);
      onChanged();
    } catch {
      /* error surfaced inline below */
    }
  }

  return (
    <>
      <tr style={{ opacity: office.active ? 1 : 0.6 }}>
        <td style={{ fontWeight: 500, fontSize: 14 }}>
          {editing ? (
            <input
              className="input"
              style={{ width: '100%' }}
              value={draft}
              autoFocus
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void saveName();
                if (e.key === 'Escape') {
                  setEditing(false);
                  setDraft(office.name);
                }
              }}
              aria-label={t('offices_col_name')}
            />
          ) : (
            office.name
          )}
        </td>
        <td className="mono meta" style={{ fontSize: 13 }}>
          {office.code}
        </td>
        <td>
          <span className={`pill ${office.active ? 'green' : 'red'}`}>
            {office.active ? t('offices_status_active') : t('offices_status_inactive')}
          </span>
        </td>
        <td>
          <div className="row gap-6">
            {editing ? (
              <>
                <button type="button" className="btn sm primary" onClick={() => void saveName()} disabled={busy}>
                  {t('btn_save')}
                </button>
                <button
                  type="button"
                  className="btn sm"
                  onClick={() => {
                    setEditing(false);
                    setDraft(office.name);
                  }}
                  disabled={busy}
                >
                  {t('btn_cancel')}
                </button>
              </>
            ) : (
              <>
                <button type="button" className="btn sm" onClick={() => setEditing(true)} disabled={busy}>
                  {t('btn_edit')}
                </button>
                <button type="button" className="btn sm" onClick={() => void toggleActive()} disabled={busy}>
                  {office.active ? t('btn_deactivate') : t('btn_activate')}
                </button>
                <button type="button" className="btn sm red" onClick={() => void del()} disabled={busy}>
                  {t('btn_delete')}
                </button>
              </>
            )}
          </div>
        </td>
      </tr>
      {error && (
        <tr>
          <td colSpan={4} style={{ paddingTop: 0 }}>
            <ErrorState message={error.message} />
          </td>
        </tr>
      )}
    </>
  );
}

/** Offices management card — SUPER_ADMIN CRUD over the DB-managed office list. */
function OfficesCard({ t }: { t: TFunc }) {
  const { offices, reload } = useOffices();
  const create = useMutation(api.offices.create);
  const [name, setName] = useState('');

  async function add() {
    const next = name.trim();
    if (!next) return;
    try {
      await create.mutate({ name: next });
      setName('');
      reload();
    } catch {
      /* error surfaced inline below */
    }
  }

  return (
    <div className="panel" style={{ overflow: 'hidden' }}>
      <div className="row between center" style={{ padding: '12px 16px', borderBottom: '1px solid var(--line-2)' }}>
        <div className="h3">{t('offices_heading')}</div>
      </div>
      <div className="col gap-12" style={{ padding: 16 }}>
        {/* Add office */}
        <div className="row gap-8 center">
          <input
            className="input"
            style={{ flex: 1, minWidth: 0 }}
            value={name}
            placeholder={t('offices_add_placeholder')}
            aria-label={t('offices_add_placeholder')}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void add();
            }}
          />
          <button
            type="button"
            className="btn primary"
            onClick={() => void add()}
            disabled={create.loading || name.trim().length === 0}
          >
            {t('btn_add')}
          </button>
        </div>
        {create.error && <ErrorState message={create.error.message} />}

        {/* Office list */}
        {offices.length === 0 ? (
          <EmptyState title={t('offices_empty_title')} hint={t('offices_empty_hint')} />
        ) : (
          <table>
            <thead>
              <tr>
                <th>{t('offices_col_name')}</th>
                <th>{t('offices_col_code')}</th>
                <th>{t('offices_col_status')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {offices.map((o) => (
                <OfficeRow key={o.id} office={o} t={t} onChanged={reload} />
              ))}
            </tbody>
          </table>
        )}

        <div className="meta" style={{ fontSize: 13, lineHeight: 1.5 }}>
          {t('offices_hint')}
        </div>
        <div className="meta" style={{ fontSize: 13, lineHeight: 1.5 }}>
          {t('offices_delete_hint')}
        </div>
      </div>
    </div>
  );
}

export default function AdminSettings() {
  const t = useT(dict);

  return (
    <div className="cl row full" style={{ minWidth: 0 }}>
      <div className="col grow" style={{ background: 'var(--paper)', overflow: 'auto', minWidth: 0 }}>
        {/* Topbar */}
        <div
          className="row center between"
          style={{ padding: '14px 24px', borderBottom: '1px solid var(--line)', background: '#fff' }}
        >
          <div className="col">
            <div className="meta">{t('topbar_sub')}</div>
            <div className="h1">{t('heading')}</div>
          </div>
        </div>

        <div className="col gap-16" style={{ padding: '18px 24px', maxWidth: 1180 }}>
          {/* Permission matrix (Area 1 §4.3) — editable by the Super Admin. */}
          <PermissionsMatrix />

          {/* Offices management */}
          <OfficesCard t={t} />

        </div>
      </div>
    </div>
  );
}
